import { useState } from "react";
import { Badge, Button, Card, Space, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { PaymentTypeOption, ProductType } from "@fitness-app/data-models/entities/Product";
import { ClientArchiveReason, type ProductClient } from "@fitness-app/data-models/entities/ProductClient";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { FREE_STATUS_MAPPER, ONE_TIME_STATUS_MAPPER } from "~/modules/Products/constants/oneTimeStatusMapper";
import { PaymentTypeTag } from "~/modules/Products/Product/ProductClientsList/components/PaymentTypeTag";
import ProductOneTimePaymentInfo from "~/modules/Products/Product/ProductClientsList/components/ProductOneTimePaymentInfo/ProductOneTimePaymentInfo";
import ProductSubscriptionInfo from "~/modules/Products/Product/ProductClientsList/components/ProductSubscriptionInfo/ProductSubscriptionInfo";
import { TraineeRenewalLink } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeRenewalLink";
import { ClientPackageDrawer } from "../components/ClientPackageDrawer/ClientPackageDrawer";
import { useTraineeProfile } from "~/modules/Trainee/TraineeProfile/scope";
import { ScheduledAccessTag } from "../components/TraineeAccess";
import { useTraineeProductClients } from "./useTraineeProductClients";

const TraineeProducts = () => {
  const { t } = useTranslation(["products", "trainees"]);
  const traineeEmail = useTraineeProfile().data?.email;
  const { isTrainer, isTeamMember } = useUserRole();
  // Oba wejścia z tej tabeli otwierają teraz drawer, więc nie ma tu już nic
  // bramkowanego `products.manage` — przycinaniem pod rolę zajmują się ekrany
  // osadzone w drawerze.

  const { data: list, isFetching } = useTraineeProductClients();
  const [openedPackageProductId, setOpenedPackageProductId] = useState<string | null>(null);

  return (
    <Card bordered={false} className="!shadow-none">
      <div className="p-4">
        <Table<ProductClient>
          dataSource={list}
          rowKey="email"
          size="middle"
          scroll={{ x: true }}
          locale={{
            emptyText: t("productClients.emptyState"),
          }}
          loading={isFetching}
          pagination={false}
        >
          <Table.Column<ProductClient>
            title={t("productTransactions.tableHeader.index")}
            width={50}
            dataIndex="index"
            key="index"
            render={(name, row, i) => i + 1}
          />
          <Table.Column<ProductClient>
            title={t("productDetails.name")}
            dataIndex="name"
            key="name"
            render={(_, row) => row.product.name}
          />
          <Table.Column
            title={t("productClients.tableHeader.createdAt")}
            dataIndex="createdAt"
            key="createdAt"
            render={(createdAt: string) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
          />
          <Table.Column<ProductClient>
            title={t("productTransactions.tableHeader.status")}
            dataIndex="status"
            key="status"
            render={(status: ProductClient["status"], productClient) =>
              productClient.paymentType === PaymentTypeOption.Free ? (
                <Badge
                  className="whitespace-nowrap"
                  status={FREE_STATUS_MAPPER[status]}
                  text={t(`common:freeAccessStatus.${status}`)}
                />
              ) : (
                <Badge
                  className="whitespace-nowrap"
                  status={ONE_TIME_STATUS_MAPPER[status]}
                  text={
                    productClient.archivedInfo?.reason === ClientArchiveReason.Refund
                      ? t(`common:accessStatus.refunded`)
                      : productClient.paymentType === PaymentTypeOption.Recurring
                        ? t(`common:subscriptionStatus.${status}`)
                        : t(`common:accessStatus.${status}`)
                  }
                />
              )
            }
          />
          <Table.Column<ProductClient>
            title={t("productClients.tableHeader.paymentType")}
            dataIndex="paymentType"
            key="paymentType"
            render={(type: (typeof PaymentTypeOption)[keyof typeof PaymentTypeOption], row) => (
              <Space>
                <PaymentTypeTag productClient={row} />
                {!row.upsellToProduct && row.paymentType === PaymentTypeOption.Recurring ? (
                  <ProductSubscriptionInfo hideCycleInfo accessPeriod={row.accessPeriod} status={row.status} />
                ) : null}
                {!row.upsellToProduct &&
                row.paymentType === PaymentTypeOption.OneTime &&
                row.accessPeriod?.type === "one_time" ? (
                  <ProductOneTimePaymentInfo accessPeriod={row.accessPeriod} showStrategyInfo />
                ) : null}
                {row.upsellToProduct && (
                  <>
                    <Tag color="green">Upsell do: {row.upsellToProduct.productName}</Tag>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setOpenedPackageProductId(row.upsellToProduct?.productId ?? null)}
                    >
                      Przejdź
                    </Button>
                  </>
                )}
              </Space>
            )}
          />

          <Table.Column<ProductClient>
            title={t("productClients.tableHeader.options")}
            dataIndex="action"
            key="action"
            render={(_, row) => {
              return (
                <Space size={4}>
                  {row.status === "active" &&
                  row.productType === ProductType.PERSONAL_CLIENT_SERVICE &&
                  (isTeamMember || isTrainer) ? (
                    "scheduledAccessPeriod" in row && row.scheduledAccessPeriod ? (
                      <ScheduledAccessTag
                        scheduledAccessPeriod={row.scheduledAccessPeriod}
                        accessPeriod={row.accessPeriod}
                      />
                    ) : (
                      <TraineeRenewalLink productClient={row} />
                    )
                  ) : null}
                  <Button type="link" onClick={() => setOpenedPackageProductId(row.productId)}>
                    {t(`common:details`)}
                  </Button>
                </Space>
              );
            }}
          />
        </Table>
      </div>
      <ClientPackageDrawer
        email={openedPackageProductId ? (traineeEmail ?? null) : null}
        productId={openedPackageProductId}
        onClose={() => setOpenedPackageProductId(null)}
      />
    </Card>
  );
};

export default TraineeProducts;
